import { Link } from "@tanstack/react-router";
import { motion, useReducedMotion } from "framer-motion";
import { Eye, MessagesSquare, ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";
import { XamseenLogo } from "@/components/xamseen-logo";

/**
 * Shared layout for every account screen.
 *
 * Two columns on large screens: a quiet brand panel that explains what the
 * member is joining, and the form itself. On phones the brand panel collapses
 * to the wordmark so the form is the first thing in reach.
 */
export function AuthShell({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const reduce = useReducedMotion();
  const rise = reduce
    ? {}
    : { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] as const } };

  return (
    <div className="min-h-screen bg-background lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
      <BrandPanel />

      <div className="flex min-h-screen items-center justify-center px-5 py-14 sm:px-8">
        <motion.div {...rise} className="w-full max-w-[400px]">
          <Link to="/" aria-label="Xamseen home" className="mb-10 inline-flex items-center lg:hidden">
            <XamseenLogo className="h-6" />
          </Link>

          <h1 className="font-display text-[28px] font-semibold leading-[1.15] tracking-tight">{title}</h1>
          {subtitle && <p className="mt-2.5 text-[15px] leading-relaxed text-muted-foreground">{subtitle}</p>}

          <div className="mt-8">{children}</div>

          {footer && <div className="mt-8 text-sm text-muted-foreground">{footer}</div>}
        </motion.div>
      </div>
    </div>
  );
}

const PROMISES: { icon: typeof Eye; title: string; body: string }[] = [
  { icon: Eye, title: "See your street", body: "What people around you are reporting, discussing and fixing right now." },
  { icon: MessagesSquare, title: "Be heard", body: "Your report joins neighbours who saw the same thing, so it carries weight." },
  { icon: ShieldCheck, title: "Stay in control", body: "You decide how precisely your location is stored and who can find you." },
];

function BrandPanel() {
  const reduce = useReducedMotion();

  return (
    <aside className="relative hidden overflow-hidden border-r border-border bg-secondary lg:flex lg:flex-col lg:justify-between lg:p-12">
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-foreground/[0.04] blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-tide/10 blur-3xl"
      />

      <Link to="/" aria-label="Xamseen home" className="relative inline-flex w-fit items-center">
        <XamseenLogo className="h-6" />
      </Link>

      <div className="relative max-w-[420px]">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
          See it. Share it. Shape it.
        </p>
        <h2 className="mt-4 font-display text-[32px] font-semibold leading-[1.15] tracking-tight">
          Your community, in focus.
        </h2>

        <ul className="mt-9 space-y-6">
          {PROMISES.map(({ icon: Icon, title, body }, i) => (
            <motion.li
              key={title}
              initial={reduce ? undefined : { opacity: 0, x: -10 }}
              animate={reduce ? undefined : { opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 + i * 0.09, ease: [0.22, 1, 0.36, 1] }}
              className="flex gap-4"
            >
              <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-border bg-background">
                <Icon className="h-4 w-4 text-plum" />
              </span>
              <div>
                <p className="text-sm font-semibold">{title}</p>
                <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">{body}</p>
              </div>
            </motion.li>
          ))}
        </ul>
      </div>

      <p className="relative text-xs leading-relaxed text-muted-foreground">
        Xamseen is not an emergency service. In an emergency, contact your local emergency number.
      </p>
    </aside>
  );
}

export function GoogleMark() {
  return (
    <svg className="mr-2.5 h-[18px] w-[18px]" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.33v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.11z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

export function AppleMark() {
  return (
    <svg className="mr-2.5 h-[19px] w-[19px]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16.36 12.78c.02 2.62 2.3 3.49 2.33 3.5-.02.05-.36 1.24-1.2 2.46-.72 1.05-1.47 2.1-2.66 2.12-1.16.02-1.54-.69-2.87-.69-1.33 0-1.74.67-2.85.71-1.14.04-2.01-1.13-2.74-2.18-1.58-2.29-2.79-6.47-1.17-9.29.81-1.4 2.25-2.29 3.82-2.31 1.12-.02 2.17.75 2.87.75.69 0 1.98-.93 3.33-.79.57.02 2.17.21 3.2 1.57-.08.05-1.9 1.11-1.89 3.31M14.3 4.2c.62-.75 1.04-1.79.92-2.83-.92.04-2.03.61-2.67 1.36-.58.66-1.08 1.72-.94 2.74 1.02.08 2.07-.52 2.69-1.27" />
    </svg>
  );
}
